import { computed, inject, Injectable } from '@angular/core';
import { SubmissionGridRowViewModel, SubmissionGridStore } from '@program-suite/data-common';
import { LookupStore } from '@program-suite/data-common';
import { TSubmissionGridRowViewModel } from '@program-suite/data-common/program';
import { GridOptions } from '@program-suite/ui-grid';

@Injectable({
  providedIn: 'root',
})
export class SubmissionGridOptions {
  lookupStore = inject(LookupStore);
  submissionGridStore = inject(SubmissionGridStore);

  totalNumberOfItems = computed<number>(() =>
    this.submissionGridStore.resource.value() ? this.submissionGridStore.resource.value()?.totalCount ?? 0 : 0
  );

  data = computed<SubmissionGridRowViewModel[] | TSubmissionGridRowViewModel>(() =>
    this.submissionGridStore.resource.value() ? this.submissionGridStore.resource.value()?.rows ?? [] : []
  );
AdminOptions = <GridOptions>{
  filterData: [
    { name: 'From', formControlName: 'dateFrom', type: 'date', dateOffset: -365 },
    { name: 'To', formControlName: 'dateTo', type: 'date', dateOffset: 0 },
    { name: 'Search', formControlName: 'searchTerm', type: 'search' },
    { name: 'Type', formControlName: 'typeName', type: 'dropdown', lookup: this.lookupStore.submissionTypes },
    { name: 'Status', formControlName: 'statusId', type: 'dropdown', lookup: this.lookupStore.submissionStatuses },
    { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup: this.lookupStore.programs },
    { name: 'Tenant', formControlName: 'tenantId', type: 'dropdown', lookup: this.lookupStore.tenants },
    { name: 'Agency', formControlName: 'agencyId', type: 'dropdown', lookup: this.lookupStore.agencies, dependencies: ['tenantId'] },
    { name: 'Producer', formControlName: 'producerId', type: 'dropdown', lookup: this.lookupStore.agents, dependencies: ['agencyId', 'tenantId'] },
  ],
  filterValues: this.submissionGridStore.filterValues,
  openFilters: true,
  columns: [
    { header: 'Created', controlName: 'createDate', displayPipe: 'date'},
    { header: 'Tenant', controlName: 'tenantName'},
    { header: 'Type', controlName: 'type'},
    { header: 'Status', controlName: 'status'},
    { header: 'Indication #', controlName: 'indicationNumber', minWidth: '124px'},
    { header: 'Submission #', controlName: 'submissionNumber', minWidth: '136px'},
    { header: 'Program', controlName: 'program'},
    { header: 'Business Name', controlName: 'businessName'},
    { header: 'Policy Effective Date', controlName: 'policyEffectiveDate', displayPipe: 'date'},
    { header: 'Producer Name', controlName: 'producerName'},
    { header: 'Manage', controlName: 'action', 
      actionColumnitems: [
        { type: 'icon', buttonAction: 'action',
          conditionalDisplay: { calculateDisplay: (status: string) => status == 'Archive' ? 'ellipsis' : 'eye', column: 'status' },
          disableOnColumnValue: { column: 'status', values: ['Archive'] },
         },
      ]
    }
  ],
  data: this.data,
  totalNumberOfItems: this.totalNumberOfItems,
  frontEndPagination: false,
  fetchData: false,
  displaySearch: false,
  useUtcDates: true,
}

WholesalerOptions = <GridOptions>{
  filterData: [
    { name: 'From', formControlName: 'dateFrom', type: 'date', dateOffset: -365 },
    { name: 'To', formControlName: 'dateTo', type: 'date', dateOffset: 0 },
    { name: 'Search', formControlName: 'searchTerm', type: 'search' },
    { name: 'Type', formControlName: 'TypeName', type: 'dropdown', lookup: this.lookupStore.submissionTypes },
    { name: 'Status', formControlName: 'statusId', type: 'dropdown', lookup: this.lookupStore.submissionStatuses },
    { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup: this.lookupStore.programs },
    { name: 'Agency', formControlName: 'agencyId', type: 'dropdown', lookup: this.lookupStore.agencies },
    { name: 'Producer', formControlName: 'producerId', type: 'dropdown', lookup: this.lookupStore.agents, dependencies: ['agencyId'] },
  ],
  filterValues: this.submissionGridStore.filterValues,
  openFilters: true,
  columns: [
    { header: 'Created', controlName: 'createDate', displayPipe: 'date'},
    { header: 'Type', controlName: 'type'},
    { header: 'Status', controlName: 'status'},
    { header: 'Indication #', controlName: 'indicationNumber', minWidth: '124px'},
    { header: 'Submission #', controlName: 'submissionNumber', minWidth: '136px'},
    { header: 'Program', controlName: 'program'},
    { header: 'Business Name', controlName: 'businessName'},
    { header: 'Policy Effective Date', controlName: 'policyEffectiveDate', displayPipe: 'date'},
    { header: 'Producer Name', controlName: 'producerName'},
    { header: 'Manage', controlName: 'action', 
      actionColumnitems: [
        { type: 'icon', buttonAction: 'action',
          conditionalDisplay: { calculateDisplay: (status: string) => status == 'Archive' ? 'ellipsis' : 'eye', column: 'status' },
          disableOnColumnValue: { column: 'status', values: ['Archive'] },
        },
      ]
    }
  ],
  data: this.data,
  totalNumberOfItems: this.totalNumberOfItems,
  frontEndPagination: false,
  fetchData: false,
  displaySearch: false,
  useUtcDates: true,
}

OtherOptions = <GridOptions>{
  filterData: [
    { name: 'From', formControlName: 'dateFrom', type: 'date', dateOffset: -365 },
    { name: 'To', formControlName: 'dateTo', type: 'date', dateOffset: 0 },
    { name: 'Search', formControlName: 'searchTerm', type: 'search' },
    { name: 'Type', formControlName: 'TypeName', type: 'dropdown', lookup: this.lookupStore.submissionTypes },
    { name: 'Status', formControlName: 'statusId', type: 'dropdown', lookup: this.lookupStore.submissionStatuses },
    { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup: this.lookupStore.programs },
    { name: 'Producer', formControlName: 'producerId', type: 'dropdown', lookup: this.lookupStore.agents },
  ],
  filterValues: this.submissionGridStore.filterValues,
  openFilters: true,
  columns: [
    { header: 'Created', controlName: 'createDate', displayPipe: 'date'},
    { header: 'Type', controlName: 'type'},
    { header: 'Status', controlName: 'status'},
    { header: 'Indication #', controlName: 'indicationNumber', minWidth: '124px'},
    { header: 'Submission #', controlName: 'submissionNumber', minWidth: '136px'},
    { header: 'Program', controlName: 'program'},
    { header: 'Business Name', controlName: 'businessName'},
    { header: 'Policy Effective Date', controlName: 'policyEffectiveDate', displayPipe: 'date'},
    { header: 'Producer Name', controlName: 'producerName'},
    { header: 'Manage', controlName: 'action', 
      actionColumnitems: [
        { type: 'icon', buttonAction: 'action',
          conditionalDisplay: { calculateDisplay: (status: string) => status == 'Archive' ? 'ellipsis' : 'eye', column: 'status' },
          disableOnColumnValue: { column: 'status', values: ['Archive'] },
        },
      ]
    }
  ],
  data: this.data,
  totalNumberOfItems: this.totalNumberOfItems,
  frontEndPagination: false,
  fetchData: false,
  displaySearch: false,
  useUtcDates: true,
}
}